// frontend/src/pages/public/RoomsPage.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../utils/api';
import { Clock, Users, Star, ChevronRight, MapPin } from 'lucide-react';
import BrandIcon from '../../components/BrandIcon';

// Étoiles de difficulté
function Stars({ value, max=5 }) {
  return (
    <div style={{ display:'flex', gap:2 }}>
      {Array.from({length:max},(_,i) => (
        <Star key={i} size={12}
          fill={i<value?'#f5a623':'none'}
          color={i<value?'#f5a623':'rgba(255,255,255,0.2)'}/>
      ))}
    </div>
  );
}

// Prix de départ
function StartingPrice({ prices }) {
  if (!prices?.filter(Boolean).length) return null;
  let min = Infinity;
  for (const p of prices.filter(Boolean)) {
    const t0 = p.tiers?.[0];
    if (!t0) continue;
    const price = t0.price_flat ? parseFloat(t0.price_flat) : parseFloat(t0.price_per_person||0);
    if (price > 0 && price < min) min = price;
  }
  if (!isFinite(min)) return null;
  const hasPricePer = prices.filter(Boolean).some(p => !p.tiers?.[0]?.price_flat);
  return (
    <span style={{ fontWeight:700, color:'var(--teal)', fontSize:15 }}>
      À partir de {min.toFixed(0)} €{hasPricePer ? '/pers' : ''}
    </span>
  );
}

// Carte salle
function RoomCard({ room, siteSlug }) {
  const navigate = useNavigate();
  return (
    <div style={{
      background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:16,
      overflow:'hidden', display:'flex', flexDirection:'column',
      transition:'transform 0.2s, box-shadow 0.2s', cursor:'pointer',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='0 12px 40px rgba(0,0,0,0.4)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='none'; }}>

      {/* Image */}
      <div style={{ height:200, background:'linear-gradient(135deg,#1a1a2e,#16213e)',
        display:'flex', alignItems:'center', justifyContent:'center', fontSize:64, position:'relative' }}>
        {room.image_url
          ? <img src={room.image_url} alt={room.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          : <BrandIcon size={40}/>
        }
        {room.site_name && (
          <div style={{ position:'absolute', top:10, right:10,
            background:`${room.site_color||'#7c5cfc'}cc`, color:'#fff',
            fontSize:11, padding:'3px 8px', borderRadius:99, fontWeight:600,
            display:'flex', alignItems:'center', gap:4 }}>
            <MapPin size={10}/> {room.site_name}
          </div>
        )}
      </div>

      {/* Contenu */}
      <div style={{ padding:20, flex:1, display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
          <h3 style={{ fontSize:18, fontWeight:700, lineHeight:1.3 }}>{room.name}</h3>
          <Stars value={room.difficulty||0}/>
        </div>
        <p style={{ fontSize:13, color:'var(--text-2)', marginBottom:16, lineHeight:1.6, flex:1 }}>
          {room.description || 'Une aventure unique vous attend…'}
        </p>
        <div style={{ display:'flex', gap:16, marginBottom:16 }}>
          <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:'var(--teal)' }}>
            <Clock size={13}/> {room.duration_min} min
          </span>
          <span style={{ display:'flex', alignItems:'center', gap:5, fontSize:13, color:'var(--text-2)' }}>
            <Users size={13}/> {room.min_players}–{room.max_players} joueurs
          </span>
        </div>
        <StartingPrice prices={room.prices}/>
        <div style={{ display:'flex', gap:8, marginTop:14 }}>
          <Link to={`/salles/${room.slug}`}
            style={{ flex:1, padding:'10px 0', textAlign:'center', borderRadius:8, fontSize:13,
              border:'1px solid var(--border)', color:'var(--text-2)', textDecoration:'none',
              background:'transparent' }}
            onClick={e => e.stopPropagation()}>
            En savoir plus
          </Link>
          <button
            style={{ flex:2, padding:'10px 0', borderRadius:8, fontSize:13, fontWeight:600,
              background:'linear-gradient(135deg,var(--accent),var(--teal))', color:'#fff',
              border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}
            onClick={() => navigate(`/reserver${siteSlug?'/'+siteSlug:''}?room=${room.id}`)}>
            Réserver <ChevronRight size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page catalogue ────────────────────────────────────────────
export default function RoomsPage() {
  const { siteSlug } = useParams();
  const [rooms, setRooms]   = useState([]);
  const [sites, setSites]   = useState([]);
  const [filter, setFilter] = useState(siteSlug || '');
  const [loading, setLoading] = useState(true);
  const [businessName, setBusiness] = useState('');

  useEffect(() => {
    api.get('/settings/public').then(r => setBusiness(r.data.settings?.business_name || 'Escape Game')).catch(()=>{});
    api.get('/sites').then(r => setSites(r.data.sites)).catch(()=>{});
    api.get('/rooms').then(r => {
      setRooms(r.data.rooms.filter(r => r.is_active));
      setLoading(false);
    }).catch(()=>setLoading(false));
  }, []);

  const displayed = filter
    ? rooms.filter(r => {
        const site = sites.find(s => s.id === r.site_id);
        return site?.slug === filter || r.site_id === filter;
      })
    : rooms;

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg-base)', color:'var(--text-1)' }}>
      {/* Header */}
      <div style={{ borderBottom:'1px solid var(--border)', padding:'16px 24px',
        display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ fontWeight:800, fontSize:20, display:'flex', alignItems:'center', gap:8 }}>
          <BrandIcon size={22}/>
          <span style={{ background:'linear-gradient(135deg,var(--accent-light),var(--teal))',
            WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>{businessName}</span>
        </div>
        <Link to="/reserver" style={{ color:'var(--teal)', fontSize:13, textDecoration:'none', fontWeight:600 }}>
          → Réserver
        </Link>
      </div>

      <div style={{ maxWidth:1200, margin:'0 auto', padding:'40px 24px' }}>
        {/* Hero */}
        <div style={{ textAlign:'center', marginBottom:48 }}>
          <h1 style={{ fontSize:'clamp(28px,5vw,48px)', fontWeight:800, marginBottom:12,
            background:'linear-gradient(135deg,#fff,rgba(255,255,255,0.6))',
            WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
            Nos salles
          </h1>
          <p style={{ fontSize:16, color:'var(--text-2)', maxWidth:500, margin:'0 auto' }}>
            Choisissez votre aventure parmi nos expériences immersives
          </p>
        </div>

        {/* Filtre sites */}
        {sites.length > 1 && (
          <div style={{ display:'flex', gap:8, justifyContent:'center', marginBottom:32, flexWrap:'wrap' }}>
            <button onClick={() => setFilter('')}
              style={{ padding:'8px 20px', borderRadius:99, border:'1px solid var(--border)',
                background: !filter ? 'var(--accent)' : 'transparent',
                color: !filter ? '#fff' : 'var(--text-2)', cursor:'pointer',
                fontFamily:'inherit', fontSize:13, fontWeight:600 }}>
              Tous les sites
            </button>
            {sites.map(s => (
              <button key={s.id} onClick={() => setFilter(s.slug||s.id)}
                style={{ padding:'8px 20px', borderRadius:99,
                  border:`1px solid ${filter===s.slug||filter===s.id ? s.color : 'var(--border)'}`,
                  background: (filter===s.slug||filter===s.id) ? s.color+'22' : 'transparent',
                  color: (filter===s.slug||filter===s.id) ? s.color : 'var(--text-2)',
                  cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:600,
                  display:'flex', alignItems:'center', gap:6 }}>
                <MapPin size={12}/> {s.name}
              </button>
            ))}
          </div>
        )}

        {/* Grille */}
        {loading ? (
          <div style={{ textAlign:'center', padding:60, color:'var(--text-3)' }}>Chargement…</div>
        ) : displayed.length === 0 ? (
          <div style={{ textAlign:'center', padding:60, color:'var(--text-3)' }}>Aucune salle disponible</div>
        ) : (
          <div className="rooms-grid" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:24 }}>
            {displayed.map(room => <RoomCard key={room.id} room={room} siteSlug={siteSlug}/>)}
          </div>
        )}
      </div>
    </div>
  );
}
